// =============================================================================
// lib/learnQuiz.ts — the quiz bank for gamified Learning Center.
//
// Each lesson ends with 2-3 multiple-choice questions. Passing (>= PASS_RATIO)
// marks the lesson complete, awards XP, and unlocks the next step / tier.
// Authored per real article slug (see GET /learn on prod); a generic comprehension
// set is the fallback for any future article that ships without a hand-authored
// quiz, so the game never dead-ends on new content.
// =============================================================================

export interface QuizQuestion {
  q: string;
  options: string[];
  /** index into options */
  answer: number;
  explain?: string;
}

// 0.6 (not 0.67): with 0.67, 2/3 = 0.6667 is NOT >= 0.67, so a 3-question quiz
// silently demanded a perfect 3/3 while the "you need N to pass" message said 2.
// 0.6 makes 2/3 pass and still requires 2/2 on a 2-question quiz (1/2 = 0.5 < 0.6).
export const PASS_RATIO = 0.6; // must get 2/3 (or 2/2) right to pass
export const XP_PER_LESSON = 50;

// Hand-authored quizzes keyed by article slug.
const QUIZZES: Record<string, QuizQuestion[]> = {
  'understanding-bitcoin-halving': [
    {
      q: 'Roughly how often does a Bitcoin halving occur?',
      options: ['Every year', 'Every ~4 years', 'Every 10 years', 'Only once'],
      answer: 1,
      explain: 'A halving happens about every four years (every 210,000 blocks).',
    },
    {
      q: 'What does a halving do to the block reward miners receive?',
      options: ['Doubles it', 'Cuts it by 50%', 'Removes it', 'Leaves it unchanged'],
      answer: 1,
      explain: 'Each halving reduces the block subsidy by 50%.',
    },
    {
      q: 'Why does the halving matter for Bitcoin?',
      options: [
        'It speeds up transactions',
        "It's a deflationary mechanism central to Bitcoin's monetary policy",
        'It lowers fees to zero',
        'It changes the logo',
      ],
      answer: 1,
      explain: 'It controls new supply issuance — the core of the deflationary design.',
    },
  ],
  'how-to-read-crypto-charts': [
    {
      q: 'On a candlestick chart, what does a single candle represent?',
      options: [
        'Only the closing price',
        'The open, high, low and close over a period',
        'The total supply',
        'The number of holders',
      ],
      answer: 1,
      explain: 'Each candle encodes open/high/low/close for its timeframe.',
    },
    {
      q: 'What does trading "volume" tell you?',
      options: [
        'How loud the market is',
        'How much of an asset traded in a period',
        'The coin’s market cap',
        'The block time',
      ],
      answer: 1,
      explain: 'Volume is the amount traded — it gauges conviction behind a move.',
    },
    {
      q: 'A "support" level is best described as…',
      options: [
        'A price where buyers tend to step in',
        'A price where an asset is delisted',
        'The all-time high',
        'The mining difficulty',
      ],
      answer: 0,
      explain: 'Support is a zone where demand has historically absorbed selling.',
    },
  ],
  'crypto-tax-guide-2026': [
    {
      q: 'In most jurisdictions, selling crypto for a profit is generally…',
      options: ['Always tax-free', 'A taxable event', 'Illegal', 'Only taxed after 10 years'],
      answer: 1,
      explain: 'Disposals that realize a gain are typically taxable events.',
    },
    {
      q: 'Which of these is usually the most important thing to keep for taxes?',
      options: [
        'Screenshots of prices',
        'Accurate records of cost basis and transactions',
        'Your seed phrase',
        'The exchange’s logo',
      ],
      answer: 1,
      explain: 'Cost basis + transaction history determine your gain/loss.',
    },
  ],
  'layer-2-solutions-explained': [
    {
      q: 'What problem do Layer 2 solutions primarily address?',
      options: [
        'Wallet design',
        'Scalability — throughput and fees on the base chain',
        'Token names',
        'Email notifications',
      ],
      answer: 1,
      explain: 'L2s scale a Layer 1 by moving execution off-chain while inheriting its security.',
    },
    {
      q: 'Where do most Layer 2s ultimately settle / anchor their security?',
      options: ['A central server', 'The Layer 1 chain', 'A bank', 'Nowhere'],
      answer: 1,
      explain: 'L2s post data/proofs back to L1, inheriting its security.',
    },
    {
      q: 'A rollup works by…',
      options: [
        'Deleting old transactions',
        'Bundling many transactions and posting them to L1',
        'Printing new coins',
        'Randomly picking validators',
      ],
      answer: 1,
      explain: 'Rollups batch (roll up) transactions and commit them to L1.',
    },
  ],
  'defi-yield-farming-risks-rewards': [
    {
      q: 'Yield farming generally means…',
      options: [
        'Mining Bitcoin with GPUs',
        'Providing liquidity / staking to earn rewards',
        'Buying and holding only',
        'Trading NFTs',
      ],
      answer: 1,
      explain: 'You supply assets to protocols to earn yield/incentives.',
    },
    {
      q: 'Which is a real risk unique to providing liquidity?',
      options: ['Impermanent loss', 'Gravity', 'Slow email', 'Low screen brightness'],
      answer: 0,
      explain: 'Price divergence between pooled assets causes impermanent loss.',
    },
    {
      q: 'A very high advertised APY usually signals…',
      options: [
        'Guaranteed profit',
        'Higher risk that must be evaluated',
        'A bug in the app',
        'Nothing at all',
      ],
      answer: 1,
      explain: 'Outsized yields typically come with outsized (often smart-contract or token) risk.',
    },
  ],
};

// Generic comprehension fallback so any un-authored article still has a quiz.
function genericQuiz(title: string): QuizQuestion[] {
  const t = title || 'this lesson';
  return [
    {
      q: `What best describes the goal of "${t}"?`,
      options: [
        'To teach a crypto concept clearly',
        'To sell you a specific coin',
        'To collect your password',
        'None of the above',
      ],
      answer: 0,
    },
    {
      q: 'Before acting on any crypto lesson, you should…',
      options: [
        'Do your own research and manage risk',
        'Invest everything immediately',
        'Ignore fees entirely',
        'Share your seed phrase',
      ],
      answer: 0,
    },
  ];
}

export function quizForArticle(slug?: string, title?: string): QuizQuestion[] {
  if (slug && QUIZZES[slug]) return QUIZZES[slug];
  return genericQuiz(title || '');
}

export function hasAuthoredQuiz(slug?: string): boolean {
  return !!(slug && QUIZZES[slug]);
}
